import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Item {
  id: number;
  title: string;
  category: string;
  condition: string;
  owner: string;
  location: string;
  distance: string;
  image: string;
  description: string;
  postedDate: string;
  interested: number;
}

interface ItemsGridProps {
  items: Item[];
  viewMode: 'grid' | 'list';
}

export default function ItemsGrid({ items, viewMode }: ItemsGridProps) {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  const toggleFavorite = (id: number) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  const handleItemClick = (id: number) => {
    navigate(`/item/${id}`);
  };

  const handleViewDetails = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    navigate(`/item/${id}`);
  };

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
        <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
          <i className="ri-search-line text-4xl text-gray-400"></i>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No items found</h3>
        <p className="text-gray-600">Try adjusting your search or filters</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {items.map(item => (
          <div
            key={item.id}
            onClick={() => handleItemClick(item.id)}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
          >
            <div className="flex gap-6 p-6">
              {/* Image */}
              <div className="relative w-48 h-48 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 right-3 px-3 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
                  {item.condition}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                        {item.category}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(item.id);
                    }}
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <i className={`${favorites.has(item.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line'} text-xl`}></i>
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {item.description}
                </p>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-base"></i>
                    <span>{item.location}</span>
                  </div>
                  <span className="text-gray-400">•</span>
                  <span className="text-teal-600 font-medium">{item.distance}</span>
                  <span className="text-gray-400">•</span>
                  <span>{item.postedDate}</span>
                </div>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-user-line text-base"></i>
                      <span>{item.owner}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <i className="ri-heart-fill text-red-400"></i>
                      <span>{item.interested}</span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => handleViewDetails(e, item.id)}
                    className="px-6 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map(item => (
        <div
          key={item.id}
          onClick={() => handleItemClick(item.id)}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
        >
          {/* Image */}
          <div className="relative w-full h-56 overflow-hidden bg-gray-100">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute top-3 right-3 px-3 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
              {item.condition}
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-base font-semibold text-gray-900 line-clamp-1">
                {item.title}
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(item.id);
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
              >
                <i className={`${favorites.has(item.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line'} text-lg`}></i>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {item.description}
            </p>

            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                {item.category}
              </span>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <i className="ri-map-pin-line text-base"></i>
              <span>{item.location}</span>
              <span className="text-gray-400">•</span>
              <span className="text-teal-600 font-medium">{item.distance}</span>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-user-line text-base"></i>
                <span>{item.owner}</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <i className="ri-heart-fill text-red-400"></i>
                <span>{item.interested}</span>
              </div>
            </div>

            <button
              onClick={(e) => handleViewDetails(e, item.id)}
              className="w-full mt-4 px-4 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
            >
              View Details
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
